import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { SearchIcon } from "@heroicons/react/outline";
import { ReactComponent as FilterIcon } from "Assets/Filter.svg";
import { ReactComponent as PasswordIcon } from "Assets/PatientPassword.svg";
import { UserAuthenticationModal } from "Pages/Patients/MainPage/UserModal";
import { isPatientGateUnlocked } from "Helpers/PatientGate";
import { PATIENT_SEARCH_STATE_KEY } from "Constants/PatientSearch";
import { BASE_PATIENT_PATH } from "Constants/Views";

// Patient search lifted into the top nav so it works from every route, not
// only from the patients page.
//
// HOW THE QUERY REACHES THE LIST: written to the sessionStorage key the
// patients page already restores from on mount (readStoredSearchState), and the
// navigation carries only a timestamp in react-router LOCATION STATE to tell an
// already-mounted page to re-read.
//
// THE QUERY NEVER GOES IN THE URL. A patient search is a name or an email, and
// the patients page says so explicitly: "never the URL — the query is PHI". A
// query string would leak it into browser history, Referer headers on every
// subsequent request, and access logs. Location state is in-memory only.
//
// THE PASSWORD GATE IS ENFORCED HERE TOO. Prompting on focus rather than after
// the search means the admin unlocks before typing a patient name, instead of
// typing one and then being stopped. It is the SAME modal and the same
// PatientRouteLogin call the patients page uses, so unlocking here starts the
// same 24h window — this is an earlier prompt, never a way around the gate.

const MIN_QUERY = 2;
// Long enough that a typed email is one or two requests rather than twenty,
// short enough to feel live.
const DEBOUNCE_MS = 400;

export default function GlobalPatientSearch() {
  const history = useHistory();
  const location = useLocation();
  const inputRef = useRef(null);
  // Seeded from storage, NOT empty. Each page renders its own MainLayout, so
  // navigating to the patient list unmounts this Navbar and mounts a fresh
  // one — with a plain useState("") the box would blank itself and lose focus
  // the moment the first keystroke routed there, which reads as "the search
  // does not work".
  const [query, setQuery] = useState(() => {
    try {
      const raw = sessionStorage.getItem(PATIENT_SEARCH_STATE_KEY);
      return raw ? String(JSON.parse(raw)?.q ?? "") : "";
    } catch {
      return "";
    }
  });
  const [gateOpen, setGateOpen] = useState(false);
  // A dismissed prompt must not immediately reopen when focus returns to the
  // field, or the box becomes impossible to click away from.
  const dismissedRef = useRef(false);
  // Whether the CURRENT value came from the admin typing, as opposed to being
  // restored from storage on mount. The box is seeded from sessionStorage so
  // the query survives the Navbar remount, and each page renders its own
  // MainLayout — so without this, opening a patient DETAIL page remounts the
  // Navbar with a stored query, the debounce fires, and the admin is thrown
  // straight back to the list. Restoring text must never navigate.
  const typedRef = useRef(false);

  const navigateToResults = ({ openFilters = false, q = query } = {}) => {
    const trimmed = String(q).trim();
    // The filter icon is allowed through with an empty box — "show me the
    // list, filtered" is a reasonable intent on its own.
    if (!openFilters && trimmed.length < MIN_QUERY) return;

    try {
      // Merge rather than replace: a DOB range or filter already stored from a
      // previous search should survive, but a NEW query always starts at page 1.
      const raw = sessionStorage.getItem(PATIENT_SEARCH_STATE_KEY);
      const stored = raw ? JSON.parse(raw) : {};
      sessionStorage.setItem(
        PATIENT_SEARCH_STATE_KEY,
        JSON.stringify({ ...stored, q: trimmed, page: 1 }),
      );
    } catch {
      // Private mode / storage disabled — nothing to restore, but the
      // navigation below still lands the admin on the list.
    }

    // Location state only: no PHI in the URL. `at` changes on every submit so
    // searching the SAME term twice still re-runs.
    const nav = { patientSearch: { at: Date.now(), openFilters } };
    // Already on the list (live typing): REPLACE, so a ten-character email does
    // not leave ten entries in history that Back has to walk out of one at a
    // time. Arriving from elsewhere: PUSH, so Back returns where they were.
    if (location.pathname === BASE_PATIENT_PATH) history.replace(BASE_PATIENT_PATH, nav);
    else history.push(BASE_PATIENT_PATH, nav);
  };

  // Restore the caret after a remount caused by the search itself navigating,
  // so typing continues uninterrupted across the route change.
  useEffect(() => {
    if (query.trim().length >= MIN_QUERY && location.pathname === BASE_PATIENT_PATH) {
      const el = inputRef.current;
      if (el && document.activeElement !== el) {
        el.focus();
        // Caret to the end rather than selecting the text, so the next
        // keystroke appends instead of replacing what they typed.
        const end = el.value.length;
        try { el.setSelectionRange(end, end); } catch { /* type=search may refuse */ }
      }
    }
    // Mount-only: re-running on every keystroke would fight a deliberate click
    // into another field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live search as they type. The old page-level SearchBar had a typeahead;
  // this replaces it, but drives the actual results table rather than a
  // separate dropdown — one list, one source of truth.
  //
  // Deliberately does nothing while the gate is locked or the modal is open:
  // typing must never fire a patient query before the password is accepted.
  useEffect(() => {
    const trimmed = query.trim();
    if (!typedRef.current) return; // restored value, not a new search
    if (trimmed.length < MIN_QUERY) return;
    if (gateOpen || !isPatientGateUnlocked()) return;

    const id = setTimeout(() => navigateToResults({ q: trimmed }), DEBOUNCE_MS);
    // Cleanup cancels the pending search on every keystroke, so only the pause
    // at the end of typing actually issues a request.
    return () => clearTimeout(id);
    // navigateToResults is redefined each render; depending on it would make
    // this effect re-run constantly and defeat the debounce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, gateOpen]);

  const submit = ({ openFilters = false } = {}) => {
    if (!isPatientGateUnlocked()) {
      dismissedRef.current = false;
      setGateOpen(true);
      return;
    }
    navigateToResults({ openFilters });
  };

  return (
    <>
      {/* Always present, every breakpoint. It used to be `hidden md:block`,
          which took the only patient lookup in the product away from anyone on
          a tablet or phone. Below md it flexes into whatever room the account
          menu leaves (min-w-0 so it shrinks instead of overflowing, max-w-xs
          so it does not sprawl on a tablet); at md and up it is the same fixed
          w-72 box it has always been. */}
      <div className="relative flex-1 min-w-0 mr-3 md:flex-none md:w-72 md:mr-4">
        {/* A bare bordered box with a slider icon in it did not read as
            search — the one affordance everyone looks for is the magnifier.
            Decorative only: the input beside it is the label. */}
        <SearchIcon
          className="pointer-events-none absolute inset-y-0 left-0 ml-3 my-auto h-4 w-4 text-gray-400"
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          type="search"
          value={query}
          placeholder="Search patients"
          aria-label="Search patients"
          onFocus={() => {
            // Ask for the password up front, so the admin is not stopped
            // mid-thought after typing a patient's name.
            if (!isPatientGateUnlocked() && !dismissedRef.current) setGateOpen(true);
          }}
          onChange={(e) => {
            typedRef.current = true;
            setQuery(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
          className="w-full h-9 pl-9 pr-9 text-sm rounded-md border border-gray-300 bg-gray-50 placeholder-gray-400 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-chill focus:border-blue-chill"
        />
        <button
          type="button"
          title="Filters"
          aria-label="Open patient filters"
          onClick={() => submit({ openFilters: true })}
          className="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400 hover:text-black focus:outline-none"
        >
          <FilterIcon className="h-4 w-4" />
        </button>
      </div>

      <UserAuthenticationModal
        modalOpen={gateOpen}
        icon={<PasswordIcon />}
        btnText="Continue"
        heading="Re-enter Password"
        content="To access patient information, please enter your password"
        // There is no patient table in the nav; unlocking here is what matters,
        // and the patients page reads the gate itself on mount.
        setShowPatientsTable={() => {}}
        setIsAuthModalOpen={(open) => {
          setGateOpen(open);
          if (open) return;
          // Closed. The modal has both a Cancel button and a successful
          // unlock, and they must not behave the same way.
          dismissedRef.current = true;
          if (!isPatientGateUnlocked()) return; // cancelled — leave them alone

          if (query.trim().length >= MIN_QUERY) {
            // They had already typed a name: go straight to the results.
            navigateToResults();
          } else {
            // Unlocked with an empty box — put the cursor back so they can
            // just start typing. Safe from reopening the modal: the gate is
            // open now, so onFocus does nothing.
            inputRef.current?.focus();
          }
        }}
      />
    </>
  );
}
